
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { Plant } from '../types';
import { t } from '../services/i18n';
import { 
  ArrowLeft, Activity, ShieldCheck, Droplets, Sun, 
  FlaskConical, Sparkles, Loader2, BookOpen
} from 'lucide-react';

export const PlantReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plant, setPlant] = useState<Plant | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const fetchPlant = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await storage.getPlantById(id);
          setPlant(data);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchPlant();
  }, [id]);

  const generateReport = async () => {
    if (!plant) return;
    setIsGenerating(true);
    try {
      const result = await storage.parseSmartCommand(`Gere um relatório botânico detalhado para a planta ${plant.nomeComum} (${plant.nomeCientifico}). Considere que ela está com status ${plant.status} e as seguintes observações: ${plant.observacoes}. Forneça curiosidades sobre a espécie e dicas de manutenção avançada.`);
      setReport(result.message);
    } catch (err) {
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-raizama-bg"><Loader2 className="animate-spin text-raizama-action" size={40} /></div>;
  if (!plant) return <div className="p-8 text-center text-raizama-textSec bg-raizama-bg h-screen">{t('details_not_found')}</div>;

  const getVitalityScore = () => {
    if (plant.status === 'Saudável') return 98;
    if (plant.status === 'Observação') return 65;
    return 32;
  };

  const score = getVitalityScore();

  return (
    <div className="flex flex-col min-h-screen bg-raizama-bg text-white pb-20">
      {/* Header */}
      <div className="p-6 flex items-center justify-between border-b border-white/5 bg-raizama-surface/50 backdrop-blur-md sticky top-0 z-30">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-full transition-colors">
          <ArrowLeft size={24} />
        </button>
        <div className="text-center">
          <h1 className="text-xs font-black uppercase tracking-[0.3em] text-raizama-highlight">{t('report_title')}</h1>
          <p className="text-[10px] text-raizama-textSec font-bold uppercase">{plant.nomeComum}</p>
        </div>
        <div className="w-10" />
      </div>

      <div className="p-6 space-y-8 animate-fade-in">
        {/* Vitality Score Card */}
        <div className="bg-raizama-surface rounded-[2.5rem] p-8 border border-raizama-border/20 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Activity size={120} />
          </div>
          
          <div className="relative z-10">
            <h2 className="text-[10px] font-black uppercase tracking-widest text-raizama-textSec mb-6">{t('report_health_score')}</h2>
            <div className="flex items-end gap-4">
              <span className="text-7xl font-black text-white tracking-tighter">
                {score}
              </span>
              <span className="text-raizama-action font-black text-xl mb-2">%</span>
            </div>
            <div className="mt-6 h-2 w-full bg-black/40 rounded-full overflow-hidden border border-white/5">
              <div 
                className={`h-full transition-all duration-1000 ${plant.status === 'Saudável' ? 'bg-emerald-500' : plant.status === 'Observação' ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${score}%` }}
              />
            </div>
            <p className="mt-4 text-xs text-raizama-textSec font-medium leading-relaxed">
              {t('report_vitality_desc')} {plant.status === 'Saudável' ? t('report_vitality_healthy') : t('report_vitality_warning')}
            </p>
          </div>
        </div>

        {/* Technical Specs Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-raizama-surface p-6 rounded-3xl border border-raizama-border/10 flex flex-col items-center text-center">
            <Sun className="text-orange-400 mb-3" size={28} />
            <span className="text-[9px] font-black uppercase text-raizama-textSec tracking-widest">{t('report_tech_exposure')}</span>
            <span className="text-xs font-bold mt-1">{plant.planoManejo?.exposicaoSolar || '-'}</span>
          </div>
          <div className="bg-raizama-surface p-6 rounded-3xl border border-raizama-border/10 flex flex-col items-center text-center">
            <Droplets className="text-blue-400 mb-3" size={28} />
            <span className="text-[9px] font-black uppercase text-raizama-textSec tracking-widest">{t('report_tech_hydration')}</span>
            <span className="text-xs font-bold mt-1">{plant.planoManejo?.rega || '-'}</span>
          </div>
          <div className="bg-raizama-surface p-6 rounded-3xl border border-raizama-border/10 flex flex-col items-center text-center">
            <FlaskConical className="text-raizama-action mb-3" size={28} />
            <span className="text-[9px] font-black uppercase text-raizama-textSec tracking-widest">{t('report_tech_nutrition')}</span>
            <span className="text-xs font-bold mt-1">{plant.planoManejo?.tipoAdubo || '-'}</span>
          </div>
          <div className="bg-raizama-surface p-6 rounded-3xl border border-raizama-border/10 flex flex-col items-center text-center">
            <ShieldCheck className="text-emerald-400 mb-3" size={28} />
            <span className="text-[9px] font-black uppercase text-raizama-textSec tracking-widest">{t('report_tech_immunity')}</span>
            <span className="text-xs font-bold mt-1">{plant.status === 'Saudável' ? 'Alta' : 'Comprometida'}</span>
          </div>
        </div>

        {/* AI Report Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-raizama-highlight flex items-center gap-2">
              <Sparkles size={14} /> {t('report_botanical_insight')}
            </h3>
            {!report && !isGenerating && (
              <button 
                onClick={generateReport}
                className="text-[9px] font-black uppercase text-raizama-action hover:underline"
              >
                {t('report_generate_btn')}
              </button>
            )}
          </div>

          {isGenerating ? (
            <div className="bg-raizama-surface/30 border border-dashed border-raizama-border/20 rounded-3xl p-12 flex flex-col items-center justify-center text-center animate-pulse">
              <Loader2 className="animate-spin text-raizama-action mb-4" size={32} />
              <p className="text-xs text-raizama-textSec font-bold uppercase tracking-widest">{t('report_ai_sync')}</p>
            </div>
          ) : report ? (
            <div className="bg-raizama-surface rounded-3xl p-6 border border-raizama-border/20 shadow-xl space-y-4">
              <div className="prose prose-invert prose-sm max-w-none text-raizama-textSec leading-relaxed whitespace-pre-wrap">
                {report}
              </div>
              <div className="pt-4 border-t border-white/5 flex items-center justify-between">
                <span className="text-[8px] text-raizama-textSec font-black uppercase tracking-widest">Fonte: Raizama Intelligence</span>
                <button onClick={() => setReport(null)} className="text-[8px] text-red-400 font-black uppercase">Limpar</button>
              </div>
            </div>
          ) : (
            <div className="bg-raizama-surface/50 rounded-3xl p-8 border border-raizama-border/10 text-center">
              <BookOpen className="mx-auto text-raizama-border/20 mb-4" size={40} />
              <p className="text-xs text-raizama-textSec mb-6">{t('report_ai_desc')}</p>
              <button 
                onClick={generateReport}
                className="bg-raizama-action text-white px-8 py-3 rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg shadow-raizama-action/20 hover:scale-105 active:scale-95 transition-all"
              >
                {t('report_generate_btn')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
